import React, { useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import * as permissionService from "../../services/permission.services";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { globalLoader, handleServerValidations } from "../../../../utils/commonfunction";
import * as Yup from "yup";
import CustomError from "../../../../utils/customError";

const PERMISSION_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

const PermissionAdd = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("label_sub_admin"), url: "/admin/staff/list/1" },
    { title: t("label_permission"), url: "/admin/permission/list/1" },
    { title: t("link_add"), url: "" },
  ];

  const validationSchema = Yup.object().shape({
    title: Yup.string().required(t("label_title_error") || "Title is required").trim(),
    short_slug: Yup.string().required(t("label_short_slug_error") || "Short slug is required").trim(),
    method: Yup.string().required(t("label_method_error") || "Method is required").trim(),
    route: Yup.string().required(t("label_route_error") || "Route is required").trim(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { title: "", short_slug: "", group_name: "", method: "", route: "" },
    validateOnBlur: false,
    validationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      globalLoader(true);
      setSubmitted(true);
      const slug = (values.short_slug || "").trim().toLowerCase().replace(/\s+/g, "-");
      const payload = {
        title: (values.title || "").trim(),
        route: (values.route || "").trim(),
        method: values.method || "",
        groupName: (values.group_name || "").trim(),
        slug,
        shortSlug: slug,
      };

      permissionService
        .Add(payload)
        .then((response) => {
          setSubmitting(false);
          if (response.success) {
            Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
            globalLoader(false);
            resetForm({ values: { title: "", short_slug: "", group_name: "", method: "", route: "" } });
            navigate("/admin/permission/list/1");
          } else {
            Swal.fire({ icon: "error", text: handleServerValidations(response), ...SWAL_SETTINGS });
            setSubmitted(false);
            globalLoader(false);
          }
        })
        .catch(() => {
          setSubmitting(false);
          setSubmitted(false);
          globalLoader(false);
        });
    },
  });

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <h6 className="main-content-label mb-3">{t("label_add_permissions")}</h6>
                <div className="row row-sm">
                  <div className="col-md-6 form-group">
                    <label htmlFor="title" className="text-left d-flex">
                      {t("label_title")}
                      <span className="requirestar">*</span>
                    </label>
                    <input
                      name="title"
                      id="title"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.title}
                      className="form-control"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="title" form={formik} />
                    </span>
                  </div>
                  <div className="col-md-6 form-group">
                    <label htmlFor="short_slug" className="text-left d-flex">
                      {t("label_short_slug")}
                      <span className="requirestar">*</span>
                    </label>
                    <input
                      name="short_slug"
                      id="short_slug"
                      type="text"
                      placeholder="e.g. view-dashboard"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.short_slug}
                      className="form-control"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="short_slug" form={formik} />
                    </span>
                  </div>
                  <div className="col-md-6 form-group">
                    <label htmlFor="group_name" className="text-left d-flex">
                      {t("label_group_name")}
                    </label>
                    <input
                      name="group_name"
                      id="group_name"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.group_name}
                      className="form-control"
                    />
                  </div>
                  <div className="col-md-6 form-group">
                    <label htmlFor="method" className="text-left d-flex">
                      {t("label_method")}
                      <span className="requirestar">*</span>
                    </label>
                    <select
                      name="method"
                      id="method"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.method}
                      className="form-control select-down-arrow"
                    >
                      <option value="">{t("input_select_text")}</option>
                      {PERMISSION_METHODS.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    <span className="text-danger d-flex text-left">
                      <CustomError name="method" form={formik} />
                    </span>
                  </div>
                  <div className="col-md-12 form-group">
                    <label htmlFor="route" className="text-left d-flex">
                      {t("label_route")}
                      <span className="requirestar">*</span>
                    </label>
                    <input
                      name="route"
                      id="route"
                      type="text"
                      placeholder="e.g. /admin/dashboard"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.route}
                      className="form-control"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="route" form={formik} />
                    </span>
                  </div>
                  <div className="mt-5">
                    <button className="btn btn-info mr-2" type="submit" disabled={submitted}>
                      <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                      {t("btn_submit")}
                    </button>
                    <button
                      className="btn ripple btn-secondary"
                      type="button"
                      disabled={submitted}
                      onClick={() => navigate(-1)}
                    >
                      <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                      {t("btn_cancel")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default PermissionAdd;
